'use client';

import { useEffect, useState } from 'react';

import { DropDisplay } from '~/components/drop-display';
import { Carousel, CarouselContent, CarouselItem } from '~/components/ui/carousel/carousel';

import type { EmblaCarouselType } from 'embla-carousel';
import type { DropResponseDto, ParticipationStatusDto } from '~/lib/api';
import { cn } from '@bazaar-marketplace/utils';

interface DropCarouselProps {
  shownDrops: { drop: DropResponseDto; participation: ParticipationStatusDto | undefined }[];
}

export const DropCarousel = ({ shownDrops }: DropCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [api, setApi] = useState<EmblaCarouselType | undefined>(undefined);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [snapList, setSnapList] = useState<number[]>([]);
  const [maxScroll, setMaxScroll] = useState(0);

  useEffect(() => {
    if (!api) return;

    const handleSelect = () => {
      setCurrentIndex(api.selectedScrollSnap());
    };

    function updateState() {
      if (!api) return;
      const scrollProgress = api.scrollProgress();
      const scrollSnapList = api.scrollSnapList();

      setMaxScroll((value) => Math.max(scrollProgress, value));
      setSnapList(scrollSnapList);
      setScrollProgress(scrollProgress);
    }

    const handleScroll = () => {
      updateState();
    };

    api.on('select', handleSelect);
    api.on('scroll', handleScroll);

    updateState();

    return () => {
      api.off('select', handleSelect);
      api.off('scroll', handleScroll);
    };
  }, [api]);

  const MAX_PAGINATION_DOTS_IN_ROW = 12;

  return (
    <Carousel
      className="relative w-full"
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      images={shownDrops.flatMap(({ drop }) => (drop.image ? [drop.image] : [])) || []}
      opts={{
        loop: false,
      }}
      plugins={[]}
      setApi={setApi}
    >
      <CarouselContent>
        {shownDrops.map(({ drop, participation }, index) => {
          const dropSnapAt = snapList[index] ?? 0;
          const progressWidth = snapList[1] ?? 1;
          const progressUntilThisDrop = dropSnapAt - scrollProgress;
          const PROGRESS_TRANSLATE_MULTIPLIER = 0.68;
          const relativeTranslate = (progressUntilThisDrop / progressWidth) * PROGRESS_TRANSLATE_MULTIPLIER;

          // we only load queries for drops that the user has scrolled to
          const isActive = snapList[index] != null && maxScroll + progressWidth * 0.5 > dropSnapAt;

          return (
            <CarouselItem key={drop.id} className="relative w-full">
              {/* Background Image Layer */}
              <div className="absolute inset-0">
                <div
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                  style={{
                    backgroundImage: `url(${drop.image as unknown as string})`,
                  }}
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black to-black/50 z-10" />
              </div>

              {/* Hero Content */}
              <div
                className="relative z-10 px-2"
                style={{
                  transform: `translateX(${relativeTranslate * 100}%)`,
                }}
              >
                <DropDisplay drop={drop} hasParticipated={!!participation} isActive={isActive} />
              </div>
            </CarouselItem>
          );
        })}
      </CarouselContent>

      {/* Pagination Dots */}
      <div className="relative z-20 flex justify-center">
        <div
          className="gap-x-2 gap-y-3 mt-4 min-w-[200px] grid py-1.5"
          style={{
            gridTemplateColumns: `repeat(${Math.min(MAX_PAGINATION_DOTS_IN_ROW, shownDrops.length)}, 1fr)`,
          }}
        >
          {shownDrops.length <= 1
            ? null
            : shownDrops.map((_, index) => (
                <div
                  key={index}
                  className={cn('flex items-center', index === currentIndex ? 'min-w-[16px]' : 'min-w-[2px]')}
                  onClick={() => {
                    setCurrentIndex(index);
                  }}
                >
                  <div
                    className={`w-full h-0.5 rounded-full transition-colors duration-300 ${
                      index === currentIndex ? 'bg-white' : 'bg-white/30'
                    }`}
                  />
                </div>
              ))}
        </div>
      </div>
    </Carousel>
  );
};
